import { useConnectionAction } from "./useConnectionAction";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@components/ui/button";
import { Label } from "@components/ui/label";
import { FormChoiceSelect } from "@components/ui/choice-select";
import { createIntegrationConnection, updateIntegrationConnection, type IntegrationConnection } from "../../lib/integrations";
import { FormField } from "./FormField";
import { shortcutFormSchema, type ShortcutForm } from "./integrationFormSchemas";

export function ShortcutConnectionCard({ connection, onSaved, onCancel }: { connection?: IntegrationConnection; onSaved: () => Promise<void>; onCancel: () => void }) {
  const action = useConnectionAction();
  const isSubmitting = action.pending;
  const formSchema = shortcutFormSchema.superRefine((values, ctx) => {
    if (!connection && values.apiToken.trim().length < 16) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["apiToken"], message: "API token must contain at least 16 characters." });
    }
  });
  const { register, control, handleSubmit, formState: { errors } } = useForm<ShortcutForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: connection?.name ?? "Shortcut",
      apiToken: "",
      workflowStateId: String(connection?.configSummary.workflowStateId ?? ""),
      storyType: (connection?.configSummary.storyType as ShortcutForm["storyType"] | undefined) ?? "feature",
      searchQuery: connection?.configSummary.searchQuery ?? "!is:archived",
    },
  });

  const submit = handleSubmit((values) => action.run("save", async () => {
    const config = {
      workflowStateId: Number(values.workflowStateId),
      storyType: values.storyType,
      searchQuery: values.searchQuery,
      ...(values.apiToken ? { apiToken: values.apiToken } : {}),
    };
    try {
      if (connection) await updateIntegrationConnection(connection.id, { name: values.name, config });
      else await createIntegrationConnection({ provider: "shortcut", name: values.name, config: { ...config, apiToken: values.apiToken }, mappings: {} });
      toast.success(connection ? "Shortcut settings updated." : "Shortcut connected.");
      await onSaved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to connect Shortcut.");
    }
  }));

  return (
    <form className="space-y-4" onSubmit={submit} noValidate>
      <div className="rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
        Shortcut currently issues account API tokens with complete account access. IO Spoke encrypts the token and never returns it.
      </div>
      <FormField id="shortcut-name" label="Connection name" registration={register("name")} error={errors.name} />
      <FormField id="shortcut-state" label="Create in workflow state ID" registration={register("workflowStateId")} error={errors.workflowStateId} />
      <div className="space-y-2">
        <Label htmlFor="shortcut-type">Story type</Label>
        <FormChoiceSelect id="shortcut-type" control={control} name="storyType" options={[{ value: "feature", label: "Feature" }, { value: "bug", label: "Bug" }, { value: "chore", label: "Chore" }]} />
      </div>
      <FormField id="shortcut-query" label="Story search query" registration={register("searchQuery")} error={errors.searchQuery} />
      <FormField
        id="shortcut-token"
        label={`API token ${connection ? "(leave blank to keep current)" : ""}`}
        registration={register("apiToken")}
        error={errors.apiToken}
        type="password"
        autoComplete="new-password"
      />
      <div className="flex gap-2">
        <Button type="submit" disabled={isSubmitting} loading={action.saving} loadingLabel={"Saving…"}>
          {connection ? "Save settings" : "Connect Shortcut"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
